import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { panel } from "@/admin/kit";

const AMBER = "#E2A03F";

export const preflightItems = (r) => {
  const links = r.links || {};
  return [
    { key: "artwork", label: "Cover artwork", ok: !!r.artwork_url, section: "artwork" },
    { key: "date", label: "Release date & time", ok: !!r.release_at, section: "schedule" },
    { key: "presave", label: "Pre-save link", ok: !!r.presave, section: "links" },
    { key: "platforms", label: "Spotify + Apple Music links (or a smart link)", ok: !!((links.spotify && links.appleMusic) || r.smart_link), section: "links" },
    { key: "audio", label: "Song audio (90-second teaser now · full song on release day)", ok: !!(r.audio_media_id || r.preview_url), section: "audio" },
    { key: "blurb", label: "Short blurb or announcement text", ok: !!((r.blurb || "").trim() || (r.announcement || "").trim()), section: "details" },
    { key: "email", label: "Release-day email armed", ok: r.announce_on_publish !== false, section: "schedule" },
    { key: "video", label: "Music video attached", ok: !!(r.video?.primary || r.video?.widescreen || (r.video?.youtube_url || "").trim()), section: "video" },
  ];
};

export const preflightScore = (r) => {
  const items = preflightItems(r);
  const ready = items.filter((i) => i.ok).length;
  return { items, ready, total: items.length, complete: ready === items.length };
};

export const PreflightBadge = ({ release }) => {
  const { ready, total, complete } = preflightScore(release);
  const color = complete ? "var(--gold)" : AMBER;
  return (
    <span className="font-caps inline-flex items-center gap-1" title={complete ? "Ready to drop" : `${total - ready} item${total - ready === 1 ? "" : "s"} still missing`}
          style={{ fontSize: ".55rem", color, border: `1px solid ${color}`, borderRadius: "var(--radius)", padding: ".25rem .5rem", letterSpacing: ".14em", fontWeight: 700 }}
          data-testid={`preflight-badge-${release.id}`} data-ready={ready} data-total={total}>
      {complete ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />} {ready}/{total} READY
    </span>
  );
};

export const PreflightCard = ({ release, onFix, onEmail, emailing }) => {
  const { items, ready, total, complete } = preflightScore(release);
  return (
    <div className="p-5 mb-5" style={{ ...panel, borderColor: complete ? "rgba(200,155,60,.5)" : "rgba(226,160,63,.5)" }} data-testid="preflight-card" data-ready={ready}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="font-caps text-gold" style={{ fontSize: ".62rem" }}>Release-day pre-flight</p>
        <div className="flex items-center gap-3">
          {onEmail && (
            <button type="button" onClick={onEmail} disabled={emailing} className="font-caps link-underline" data-testid="preflight-email"
                    style={{ fontSize: ".55rem", color: "var(--gold)", background: "none", border: "none", cursor: "pointer", letterSpacing: ".16em" }}>
              {emailing ? "Sending…" : "Email me this list"}
            </button>
          )}
          <span className="font-caps" style={{ fontSize: ".6rem", color: complete ? "var(--gold-lift)" : AMBER }} data-testid="preflight-score">
            {complete ? "All clear — ready to drop" : `${ready}/${total} ready`}
          </span>
        </div>
      </div>
      <ul className="grid gap-1.5" data-testid="preflight-list">
        {items.map((i) => (
          <li key={i.key} className="flex items-center justify-between gap-3" style={{ fontSize: ".82rem" }} data-testid={`preflight-${i.key}`} data-ok={i.ok}>
            <span className="inline-flex items-center gap-2" style={{ color: i.ok ? "var(--parchment)" : AMBER }}>
              {i.ok ? <CheckCircle2 size={14} color="var(--gold)" /> : <AlertCircle size={14} color={AMBER} />} {i.label}
            </span>
            {!i.ok && (
              <button type="button" onClick={() => onFix(i.section)} className="font-caps link-underline" data-testid={`preflight-fix-${i.key}`}
                      style={{ fontSize: ".55rem", color: AMBER, background: "none", border: "none", cursor: "pointer", letterSpacing: ".16em" }}>
                Fix
              </button>
            )}
          </li>
        ))}
      </ul>
      {!complete && <p className="mt-3" style={{ color: "var(--ash)", fontSize: ".72rem" }}>Amber items are missing. Fix them, then Save. You&apos;ll also get this list by email 3 days before the drop if anything is still open. <Link to="/admin/upcoming" className="link-underline" style={{ color: "var(--gold)" }}>Back to Upcoming</Link></p>}
    </div>
  );
};
